import {
    IJobRunDetail,
    IJobTaskRunDetail,
    JobStatus,
    JobTaskStatus,
    JobTaskType,
} from "@/api/admin/admin.types";
import { intervalToDuration } from "date-fns";
import { keyframes } from "@emotion/react";

// --- Phase Definitions ---

export interface PhaseDefinition {
    key: string;
    label: string;
    taskTypes: JobTaskType[];
}

export const PHASES: PhaseDefinition[] = [
    {
        key: "clause_and_field_extraction",
        label: "Clause & Field Extraction",
        taskTypes: [
            JobTaskType.CLAUSE_ANALYSIS_BATCH,
            JobTaskType.FIELD_EXTRACTION,
            JobTaskType.FORM_EXTRACTION,
        ],
    },
    {
        key: "topic_summaries",
        label: "Topic Summaries",
        taskTypes: [JobTaskType.TOPIC_SUMMARY],
    },
    {
        key: "headline",
        label: "Headline",
        taskTypes: [JobTaskType.SCOPE_HEADLINE],
    },
];

// --- Status Derivation ---

export type DerivedPhaseStatus =
    | "completed"
    | "failed"
    | "processing"
    | "queued"
    | "idle";

export const derivePhaseStatus = (
    tasks: IJobTaskRunDetail[]
): DerivedPhaseStatus => {
    if (tasks.length === 0) return "idle";

    const allCompleted = tasks.every(
        (t) => t.status === JobTaskStatus.COMPLETED
    );
    if (allCompleted) return "completed";

    const anyFailed = tasks.some(
        (t) =>
            t.status === JobTaskStatus.FAILED ||
            t.status === JobTaskStatus.CANCELLED ||
            (t.status === JobTaskStatus.RETRYABLE &&
                t.retry_count >= t.max_retries)
    );
    if (anyFailed) return "failed";

    const anyProcessing = tasks.some(
        (t) =>
            t.status === JobTaskStatus.PROCESSING ||
            (t.status === JobTaskStatus.RETRYABLE &&
                t.retry_count < t.max_retries)
    );
    if (anyProcessing) return "processing";

    const allQueued = tasks.every((t) => t.status === JobTaskStatus.QUEUED);
    if (allQueued) return "queued";

    // Mix of completed + queued (no actively processing tasks): still in progress
    const hasQueued = tasks.some((t) => t.status === JobTaskStatus.QUEUED);
    if (hasQueued) return "processing";

    return "completed";
};

// --- Phase Grouping ---

export const groupTasksByPhase = (
    tasks: IJobTaskRunDetail[]
): Map<string, IJobTaskRunDetail[]> => {
    const grouped = new Map<string, IJobTaskRunDetail[]>();
    for (const phase of PHASES) {
        grouped.set(
            phase.key,
            tasks.filter((t) => phase.taskTypes.includes(t.task_type))
        );
    }
    return grouped;
};

// --- Duration Formatting ---

export const formatCompactDuration = (
    startDate: Date,
    endDate: Date
): string => {
    const { days, hours, minutes, seconds } = intervalToDuration({
        start: startDate,
        end: endDate,
    });

    const parts: string[] = [];
    if (days) parts.push(`${days}d`);
    if (hours) parts.push(`${hours}h`);
    if (minutes) parts.push(`${minutes}m`);
    if (seconds || parts.length === 0) parts.push(`${seconds ?? 0}s`);

    return parts.join(" ");
};

export const getTaskDuration = (task: IJobTaskRunDetail): string | null => {
    if (!task.processing_at) return null;
    const end = task.completed_at || task.failed_at;
    if (!end) return null;
    return formatCompactDuration(new Date(task.processing_at), new Date(end));
};

export const getPhaseDuration = (tasks: IJobTaskRunDetail[]): string | null => {
    const processingTimes = tasks
        .map((t) => t.processing_at)
        .filter(Boolean) as string[];
    const endTimes = tasks
        .map((t) => t.completed_at || t.failed_at)
        .filter(Boolean) as string[];

    if (processingTimes.length === 0 || endTimes.length === 0) return null;

    const earliest = new Date(
        Math.min(...processingTimes.map((d) => new Date(d).getTime()))
    );
    const latest = new Date(
        Math.max(...endTimes.map((d) => new Date(d).getTime()))
    );

    return formatCompactDuration(earliest, latest);
};

export const getPhaseStartTime = (
    tasks: IJobTaskRunDetail[]
): string | null => {
    const processingTimes = tasks
        .map((t) => t.processing_at)
        .filter(Boolean) as string[];
    if (processingTimes.length === 0) return null;
    return new Date(
        Math.min(...processingTimes.map((d) => new Date(d).getTime()))
    ).toISOString();
};

export const isPhaseInProgress = (tasks: IJobTaskRunDetail[]): boolean => {
    if (tasks.length === 0) return false;
    return tasks.some(
        (t) =>
            t.status === JobTaskStatus.PROCESSING ||
            (t.status === JobTaskStatus.RETRYABLE &&
                t.retry_count < t.max_retries) ||
            t.status === JobTaskStatus.QUEUED
    );
};

// --- Terminal Status ---

const TERMINAL_STATUSES = new Set([
    JobStatus.COMPLETED,
    JobStatus.FAILED,
    JobStatus.PARTIAL_SUCCESS,
    JobStatus.CANCELLED,
]);

export const isJobTerminal = (status: JobStatus): boolean =>
    TERMINAL_STATUSES.has(status);

export const isJobNonTerminal = (job: IJobRunDetail | null): boolean => {
    if (!job) return false;
    return !isJobTerminal(job.status);
};

// --- Task Type Labels ---

export const TASK_TYPE_LABELS: Record<JobTaskType, string> = {
    [JobTaskType.CLAUSE_ANALYSIS_BATCH]: "Clause Analysis",
    [JobTaskType.FIELD_EXTRACTION]: "Field Extraction",
    [JobTaskType.FORM_EXTRACTION]: "Form Extraction",
    [JobTaskType.TOPIC_SUMMARY]: "Topic Summary",
    [JobTaskType.SCOPE_HEADLINE]: "Headline",
};

const MINOR_WORDS = new Set([
    "of",
    "the",
    "and",
    "in",
    "on",
    "at",
    "to",
    "for",
    "a",
    "an",
]);

const titleCase = (snakeStr: string): string =>
    snakeStr
        .split("_")
        .map((w, i) =>
            i > 0 && MINOR_WORDS.has(w.toLowerCase())
                ? w.toLowerCase()
                : w.charAt(0).toUpperCase() + w.slice(1)
        )
        .join(" ");

export const getTaskMetadataLabel = (
    task: IJobTaskRunDetail
): string | null => {
    const meta = task.metadata_;
    if (!meta) return null;

    switch (task.task_type) {
        case JobTaskType.CLAUSE_ANALYSIS_BATCH:
            return meta.batch_size
                ? `${meta.batch_size} clause${meta.batch_size === 1 ? "" : "s"}`
                : null;
        case JobTaskType.FIELD_EXTRACTION: {
            if (meta.field_definition_ids) {
                const count = meta.field_definition_ids.length;
                if (count === 1 && meta.field_definitions?.[0]?.name) {
                    return meta.field_definitions[0].name;
                }
                return `${count} field${count === 1 ? "" : "s"}`;
            }
            return (
                meta.field_definition?.name ??
                (meta.field_definition_id
                    ? `Field #${meta.field_definition_id}`
                    : null)
            );
        }
        case JobTaskType.FORM_EXTRACTION:
            return meta.form_types
                ? meta.form_types.map(titleCase).join(", ")
                : null;
        case JobTaskType.TOPIC_SUMMARY:
            return meta.topic_name || null;
        default:
            return null;
    }
};

// --- Spin Animation ---

export const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

// --- Task Type Grouping ---

export const groupTasksByType = (
    tasks: IJobTaskRunDetail[]
): Map<JobTaskType, IJobTaskRunDetail[]> => {
    const grouped = new Map<JobTaskType, IJobTaskRunDetail[]>();
    for (const task of tasks) {
        const existing = grouped.get(task.task_type);
        if (existing) {
            existing.push(task);
        } else {
            grouped.set(task.task_type, [task]);
        }
    }
    return grouped;
};
